'use client';

import { useEffect } from 'react';

import { isIframeMode } from '@ps/functions/bio/iframeMode';

/**
 * Turns the bio into an inert preview when it's embedded in a host's phone
 * mockup (`?source=iframe` — express-setup's Live/Template steps, the AI bio
 * preview). Mirrors PopshopRN's `embedded` bio mode.
 *
 * Two effects, both driven by a `data-bio-iframe-preview` attribute on the
 * root element:
 *
 *   • Anything marked {@link kIframePreviewHiddenAttr} is hidden — chrome that
 *     only makes sense on the real bio (the owner preview banner, the
 *     login/account pill).
 *   • Interactive elements get `pointer-events: none`, so nothing inside the
 *     preview can be clicked, typed into, or navigated. The scroll containers
 *     keep their pointer events, so native (incl. iOS momentum) scrolling of
 *     the preview still works — which is why this isn't done by putting
 *     `pointer-events: none` on the host's `<iframe>` element.
 *
 * The attribute is set client-side on mount: the bio's shell is server-rendered
 * inside a `'use cache'` boundary, so it can't read the query param (request
 * data) at render time. The rules themselves ship in the server HTML, so they
 * apply the moment the attribute lands.
 */
const kIframePreviewRootAttr = 'data-bio-iframe-preview';

const kIframePreviewHiddenAttr = 'data-bio-iframe-hidden';

/** Spread onto any element that must not show inside an embedded preview. */
export const kIframePreviewHiddenProps = {
  [kIframePreviewHiddenAttr]: '',
} as const;

const kInertSelectors = [
  'a',
  'button',
  'input',
  'select',
  'textarea',
  'label',
  'video',
  'audio',
  'iframe',
  '[role="button"]',
  '[role="link"]',
  '[role="tab"]',
  '[contenteditable]',
] as const;

const kPreviewStyles = `
:root[${kIframePreviewRootAttr}] [${kIframePreviewHiddenAttr}] { display: none !important; }
:root[${kIframePreviewRootAttr}] body { -webkit-user-select: none; user-select: none; }
${kInertSelectors
  .map(selector => `:root[${kIframePreviewRootAttr}] ${selector}`)
  .join(',\n')} { pointer-events: none !important; }
`;

export function BioIframePreviewMode() {
  useEffect(() => {
    if (!isIframeMode()) return;
    const root = document.documentElement;
    root.setAttribute(kIframePreviewRootAttr, '');
    return () => root.removeAttribute(kIframePreviewRootAttr);
  }, []);

  return <style>{kPreviewStyles}</style>;
}
